import React from 'react'
import { getOwnerContactsByAccountIds } from '@/modules/accounts/services/ownerContactService'
import { getRewardsAppSettings } from '../services/rewardsSettingsService'
import {
  listRewardsCyclesInRange,
  listRewardsMasterLinks,
  listRewardsMetricsByCycleIds,
  listRewardsOpenCycles,
  type RewardsMasterCycle,
  type RewardsMasterMetricRow,
} from '../services/rewardsMasterService'
import type { RewardsAccountExchangeLink } from '../types'

const HISTORY_MONTHS = 60

export type RewardsMasterDashboardState = {
  loading: boolean
  error: string | null
  links: RewardsAccountExchangeLink[]
  ownerByAccount: Map<string, any>
  openMetricsByAccountExchange: Map<string, RewardsMasterMetricRow>
  historyCyclesById: Map<string, RewardsMasterCycle>
  historyMetrics: RewardsMasterMetricRow[]
  multiplierX: number
  usdtBrlRate: number
  reload: () => Promise<void>
}

export function useRewardsMasterDashboard(): RewardsMasterDashboardState {
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [links, setLinks] = React.useState<RewardsAccountExchangeLink[]>([])
  const [ownerByAccount, setOwnerByAccount] = React.useState<Map<string, any>>(new Map())
  const [openMetricsByAccountExchange, setOpenMetricsByAccountExchange] = React.useState<Map<string, RewardsMasterMetricRow>>(new Map())
  const [historyCyclesById, setHistoryCyclesById] = React.useState<Map<string, RewardsMasterCycle>>(new Map())
  const [historyMetrics, setHistoryMetrics] = React.useState<RewardsMasterMetricRow[]>([])
  const [multiplierX, setMultiplierX] = React.useState(3)
  const [usdtBrlRate, setUsdtBrlRate] = React.useState(0)

  const reload = React.useCallback(async () => {
    setError(null)
    setLoading(true)
    try {
      const [settings, linksResp] = await Promise.all([getRewardsAppSettings().catch(() => null), listRewardsMasterLinks()])

      if (settings) {
        setMultiplierX(Number(settings.multiplierX || 3))
        setUsdtBrlRate(Number(settings.usdtBrlRate || 0))
      }

      setLinks(linksResp)

      const accountIds = Array.from(new Set(linksResp.map((l) => l.account_id)))
      if (!accountIds.length) {
        setOwnerByAccount(new Map())
        setOpenMetricsByAccountExchange(new Map())
        setHistoryCyclesById(new Map())
        setHistoryMetrics([])
        return
      }

      try {
        const owners = await getOwnerContactsByAccountIds(accountIds)
        setOwnerByAccount(owners as any)
      } catch {
        setOwnerByAccount(new Map())
      }

      const historyStart = getHistoryStartIso()
      const [openCycles, historyCycles] = await Promise.all([
        listRewardsOpenCycles(accountIds),
        listRewardsCyclesInRange(accountIds, historyStart),
      ])

      const openCycleById = new Map(openCycles.map((c) => [c.id, c]))
      const historyCycleById = new Map(historyCycles.map((c) => [c.id, c]))
      setHistoryCyclesById(historyCycleById)

      const openMetrics = await listRewardsMetricsByCycleIds(openCycles.map((c) => c.id))
      const openMetricsMap = new Map<string, RewardsMasterMetricRow>()
      for (const m of openMetrics) {
        const cycle = openCycleById.get(m.cycle_id)
        if (!cycle) continue
        openMetricsMap.set(`${cycle.account_id}:${m.exchange}`, m)
      }
      setOpenMetricsByAccountExchange(openMetricsMap)

      const historyMetricsResp = await listRewardsMetricsByCycleIds(historyCycles.map((c) => c.id))
      setHistoryMetrics(historyMetricsResp)
    } catch (e: any) {
      setError(e?.message || 'Falha ao carregar dados do Rewards (MASTER).')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    reload()
  }, [reload])

  return {
    loading,
    error,
    links,
    ownerByAccount,
    openMetricsByAccountExchange,
    historyCyclesById,
    historyMetrics,
    multiplierX,
    usdtBrlRate,
    reload,
  }
}

function getHistoryStartIso(): string {
  const d = new Date()
  d.setMonth(d.getMonth() - HISTORY_MONTHS)
  d.setHours(0, 0, 0, 0)
  return d.toISOString()
}
